import React, { useMemo, useState } from 'react';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { TypographyTextColorVariant } from 'apps/sigma2-ui/src/components/ui/Typography';
import { IconSignalBarsSvg } from 'apps/sigma2-ui/src/components/svg/IconSignalBars';
import { formatNumberForDisplayInUnits } from 'libs/end-to-end/src/number-formatting';
import { NumericDisplayUnit } from 'libs/end-to-end/src/metrics';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';

export interface TableTooltipAverageProps {
  value?: number;
  averageDisplayName: string;
}

export const TableTooltip = ({
  metricDisplayName,
  metricHeaderValue,
  levelId,
  sampleSizeDisplay,
  departmentAverage,
  organizationAverage,
  confidence,
  metricUnits,
  groupByLevel,
  secondaryBreakdownLevel,
  getLinkToDeepDive,
}: {
  metricDisplayName: string;
  metricHeaderValue: string;
  levelId: string;
  sampleSizeDisplay: string;
  departmentAverage: TableTooltipAverageProps;
  organizationAverage: TableTooltipAverageProps;
  confidence?: number;
  metricUnits: NumericDisplayUnit;
  groupByLevel: string;
  secondaryBreakdownLevel: string | undefined;
  getLinkToDeepDive: (props: {
    metric: string;
    groupByLevel: string;
    secondaryBreakdownLevel: string | undefined;
  }) => Promise<string | undefined>;
}) => {
  const language = useLanguage();

  const differenceLabel = useMemo(() => {
    if (
      departmentAverage.value === undefined ||
      organizationAverage.value === undefined
    ) {
      return null;
    }

    const difference = departmentAverage.value - organizationAverage.value;
    const differenceDisplay = formatNumberForDisplayInUnits({
      value: difference,
      isPercentageAlreadyMultipliedBy100: false,
      units: metricUnits,
    });

    if (difference < 0) {
      return `${differenceDisplay} lower`;
    } else if (difference > 0) {
      return `${differenceDisplay} higher`;
    }

    return null;
  }, [departmentAverage.value, organizationAverage.value, metricUnits]);

  const confidenceSignalBars = useMemo<{
    value: number;
    color: TypographyTextColorVariant;
  } | null>(() => {
    if (confidence === undefined) {
      return null;
    }

    if (confidence >= 0.8) {
      return { value: 4, color: 'green-600' };
    } else if (confidence >= 0.6) {
      return { value: 3, color: 'yellow-500' };
    } else if (confidence >= 0.4) {
      return { value: 2, color: 'orange-500' };
    } else {
      return { value: 1, color: 'red-500' };
    }
  }, [confidence]);

  const [linkToDeepDive, setLinkToDeepDive] = useState<string | undefined>(
    undefined,
  );

  const [isLoadingDeepDiveLink, setIsLoadingDeepDiveLink] =
    useState<boolean>(false);

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isAlive) => {
      if (!isAlive()) {
        return;
      }

      setIsLoadingDeepDiveLink(true);
      const linkToDeepDive = await getLinkToDeepDive({
        metric: metricHeaderValue,
        groupByLevel,
        secondaryBreakdownLevel,
      });
      setIsLoadingDeepDiveLink(false);
      setLinkToDeepDive(linkToDeepDive);
    },
    [
      getLinkToDeepDive,
      metricHeaderValue,
      groupByLevel,
      secondaryBreakdownLevel,
    ],
  );

  const { getDimensionByLevelColumnValue, getLevelByColumnValue } =
    useDimensionConfig();

  const levelDefinition = useMemo(() => {
    const dimension = getDimensionByLevelColumnValue(levelId);

    if (!dimension) {
      return;
    }

    return getLevelByColumnValue(dimension.columnHeaderValue, levelId);
  }, [getDimensionByLevelColumnValue, getLevelByColumnValue, levelId]);

  const learnMoreButton = useMemo(() => {
    if (linkToDeepDive) {
      return (
        <TouchTarget
          content={() => {
            return (
              <div className="flex items-start h-6 mt-4 ">
                <S2Typography
                  underline
                  variant={'link'}
                  translatedContent={language.learnMore()}
                  textColorVariant={'royal-700'}
                />
              </div>
            );
          }}
          behavior={{
            type: 'href',
            href: linkToDeepDive,
          }}
        />
      );
    }

    if (isLoadingDeepDiveLink) {
      return (
        <div className="flex items-start h-6 mt-5 ">
          <Spinner size={16} />
        </div>
      );
    }

    return <></>;
  }, [isLoadingDeepDiveLink, language, linkToDeepDive]);

  return (
    <div className="flex flex-col p-2 ">
      <div className="flex items-start mb-3 gap-1.5">
        {levelDefinition && (
          <>
            <S2Typography
              variant="body-6"
              translatedContent={levelDefinition.displayName}
              textColorVariant="gray-900"
            />
            <S2Typography
              variant="body-4"
              translatedContent="&middot;"
              textColorVariant="gray-900"
              dangerouslyOverrideClassNameFn={(cn) => `${cn} !leading-6`}
            />
          </>
        )}

        <S2Typography
          variant="body-6-bold"
          translatedContent={metricDisplayName}
          textColorVariant="gray-900"
        />
      </div>
      <div className="flex w-full justify-between gap-6">
        <div className="flex flex-col whitespace-nowrap">
          <S2Typography
            variant="body-7"
            translatedContent={departmentAverage.averageDisplayName}
          />
          <div className="flex gap-2 items-center justify-between">
            {departmentAverage.value && (
              <S2Typography
                variant="body-4"
                translatedContent={formatNumberForDisplayInUnits({
                  value: departmentAverage.value,
                  isPercentageAlreadyMultipliedBy100: false,
                  units: metricUnits,
                })}
                textColorVariant="gray-900"
                dangerouslyOverrideClassNameFn={(cn) =>
                  `${cn} !leading-8 !font-bold`
                }
              />
            )}
            {differenceLabel && (
              <S2Typography
                variant="body-7"
                translatedContent={differenceLabel}
                textColorVariant="gray-400"
                dangerouslyOverrideClassNameFn={(cn) => `${cn} !font-medium`}
              />
            )}
          </div>
          <S2Typography
            variant="body-7"
            textColorVariant="gray-900"
            translatedContent={sampleSizeDisplay}
          />
        </div>

        {organizationAverage.value && (
          <div className="flex flex-col whitespace-nowrap">
            <S2Typography
              variant="body-7"
              translatedContent={organizationAverage.averageDisplayName}
            />
            <div className="flex gap-2 items-center justify-between">
              <S2Typography
                variant="body-4"
                translatedContent={formatNumberForDisplayInUnits({
                  value: organizationAverage.value,
                  isPercentageAlreadyMultipliedBy100: false,
                  units: metricUnits,
                })}
                textColorVariant="gray-400"
                dangerouslyOverrideClassNameFn={(cn) =>
                  `${cn} !leading-8 !font-bold`
                }
              />
            </div>
          </div>
        )}
      </div>
      {confidenceSignalBars && confidence && (
        <div className="flex w-full mt-4 justify-between rounded bg-gray-50 border-1 border-gray-200 px-2 py-1">
          <div className="flex items-center gap-2">
            <IconSignalBarsSvg
              svg={{ className: `text-${confidenceSignalBars.color}` }}
              numberOfBars={confidenceSignalBars.value}
            />
            <S2Typography
              variant="body-7"
              translatedContent={language.confidence()}
              textColorVariant="gray-900"
            />
            <S2Typography
              variant="body-7-bold"
              translatedContent={`${(confidence * 100).toFixed(1)}%`}
              textColorVariant={confidenceSignalBars.color}
            />
          </div>
        </div>
      )}
      {learnMoreButton}
    </div>
  );
};

export const MetricRequiresGrouping = () => {
  const language = useLanguage();
  return (
    <div className="flex flex-col p-2 ">
      <S2Typography
        variant="body-7"
        translatedContent={language.metricRequiresBreakdown()}
        textColorVariant="gray-900"
      />
    </div>
  );
};
